import { useCallback, useEffect, useRef, useState } from 'react';

export type SaveState = 'idle' | 'saving' | 'saved' | 'error';

/**
 * Autosave every 30s and on blur (§10.5).
 *
 * No spinner and no toast — a toast every 30 seconds is noise, and noise is
 * what people learn to ignore. State lives in one place, in the stamp rail:
 * `Saqlanmoqda…` → `Saqlandi 14:32`.
 */
export function useAutosave<T>({
  value,
  onSave,
  enabled = true,
  intervalMs = 30_000,
  debounceMs = 1_200,
  auto = true,
}: {
  value: T;
  onSave: (value: T) => Promise<unknown>;
  enabled?: boolean;
  intervalMs?: number;
  debounceMs?: number;
  /** false → hech qachon o'zi saqlamaydi; faqat `dirty` kuzatiladi va `flush()`
   *  (Saqlash tugmasi) qo'lda chaqiriladi. */
  auto?: boolean;
}) {
  const [state, setState] = useState<SaveState>('idle');
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [dirty, setDirty] = useState(false);

  const latest = useRef(value);
  const baseline = useRef(JSON.stringify(value));
  const inFlight = useRef(false);
  const debounce = useRef<number | undefined>(undefined);

  latest.current = value;

  // Returns true if the save succeeded (or there was nothing to save), false on
  // error — so an explicit "Save" click can show a success/failure toast.
  const flush = useCallback(async (): Promise<boolean> => {
    if (!enabled || inFlight.current) return false;
    const snapshot = JSON.stringify(latest.current);
    if (snapshot === baseline.current) return true;

    inFlight.current = true;
    setState('saving');
    try {
      await onSave(latest.current);
      // Compare against the snapshot that was sent, not the current value —
      // edits made mid-request must stay dirty rather than be marked saved.
      baseline.current = snapshot;
      setSavedAt(new Date());
      setDirty(JSON.stringify(latest.current) !== snapshot);
      setState('saved');
      return true;
    } catch {
      setState('error');
      return false;
    } finally {
      inFlight.current = false;
    }
  }, [enabled, onSave]);

  // Debounced save on change, so a burst of keystrokes is one request.
  useEffect(() => {
    if (!enabled) return undefined;
    const snapshot = JSON.stringify(value);
    if (snapshot === baseline.current) {
      setDirty(false);
      return undefined;
    }
    setDirty(true);
    // Qo'lda saqlash rejimi: o'zgarish faqat `dirty` qiladi, so'rov yubormaydi.
    if (!auto) return undefined;
    window.clearTimeout(debounce.current);
    debounce.current = window.setTimeout(() => void flush(), debounceMs);
    return () => window.clearTimeout(debounce.current);
  }, [value, enabled, flush, debounceMs, auto]);

  // The interval is a floor, for the case where someone types continuously.
  useEffect(() => {
    if (!enabled || !auto) return undefined;
    const timer = window.setInterval(() => void flush(), intervalMs);
    return () => window.clearInterval(timer);
  }, [enabled, flush, intervalMs, auto]);

  // Save on blur — switching to another tab is the most common way an edit
  // gets abandoned mid-sentence.
  useEffect(() => {
    if (!enabled || !auto) return undefined;
    const onBlur = () => void flush();
    window.addEventListener('blur', onBlur);
    return () => window.removeEventListener('blur', onBlur);
  }, [enabled, flush, auto]);

  // Closing the tab with unsaved work should cost a confirmation, not the work.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  /** Adopt a server response as the new baseline, e.g. after an explicit save. */
  const markSaved = useCallback((next?: T) => {
    if (next !== undefined) latest.current = next;
    baseline.current = JSON.stringify(latest.current);
    setSavedAt(new Date());
    setDirty(false);
    setState('saved');
  }, []);

  return { state, savedAt, dirty, flush, markSaved };
}
